import React from 'react';

import { AnalyticsEvent, EventParams, logEvent } from '@weddingspot/ws-analytics';
import { ReviewCard, OverallRating } from '@weddingspot/ws-core-components';

import { VenueProfileContext } from '../../context';

import styles from './Reviews.module.scss';
import ViewAll from './ViewAll/ViewAll';
import { REVIEW_BATCH_SIZE } from './constant';

/**
 * Reviews component can be used to display a list of reviews for a venue profile.
 * This component reads the reviews from context and displays them in either a Card view or Plain view.
 * <pre>
 * Sample input
 {
        overallRating: 4.5,
        totalCount: 7,
        data: [
            {
                rating: {
                    name: 'Rating',
                    readonly: true,
                    stars: 3,
                },
                review: {
                    author: 'John doe',
                    text: 'This place is awesome',
                },
                showAsCard: true,
                isNew: true,
            },
        ],
    }
 * </pre>
 */
const Reviews: React.FC = () => {
    const { reviews = { data: [] }, venueId } = React.useContext(VenueProfileContext);
    const [initialized, setInitialized] = React.useState(false);
    
    // log the review loaded event
    React.useEffect(() => {
        if (!initialized && reviews.data.length > 0) {
            logEvent(AnalyticsEvent.EVNTM_LOADED_VENUE_PROFILE_REVIEWS, {
                Source: 'Venue Profile V3',
                [EventParams.VENUE_ID]: venueId,
            });
            setInitialized(true);
        }
    }, [reviews, venueId, initialized]);

    return (
        reviews.data.length > 0 && (
            <div className={styles.reviewContainer}>
                <h2>Reviews</h2>
                {reviews.overallRating && reviews.totalCount > REVIEW_BATCH_SIZE - 1 && (
                    <OverallRating totalCount={reviews.totalCount} overallRating={reviews.overallRating} />
                )}
                {reviews.data.slice(0, REVIEW_BATCH_SIZE).map(({ review, rating}, index) => (
                    <ReviewCard key={review.reviewId} review={review} rating={rating} isLast={index == reviews.data.length - 1 || index == REVIEW_BATCH_SIZE - 1} />
                ))}
                {reviews.totalCount > REVIEW_BATCH_SIZE && <ViewAll />}
            </div>
        )
    );
};

export default Reviews;
